import { create, EndpointCall, retrieve, update, EndpointResponseFactory, delete$ } from "components/http/endpoint";
import { create as createRequest } from "components/http/rest/jsonRequest";
import { apiTransportDateToDate, dateToApiTransportDate } from "components/http/transport"
import { MemberApplication } from "./entity";
import { ApiUriPrefix } from "../apiUriPrefix";
import { EntityJSONResponse } from "components/http/rest/jsonResponse";
import { AjaxResponse } from "components/http/ajax/types";
import { Entity } from "components/state/entity";
import { ODataParameters, retrieveCollection } from "components/http/rest/restEndpoint";
import { EntityResponse } from "components/validation/validatableentity"

const ApplicationApiUri = ApiUriPrefix + "members/applications";
const ClubApplicationApiUri = ApiUriPrefix + "clubs/<clubID>/member_applications";

function getBaseUri(clubID: number): string {
    if (clubID != null) {
        return ClubApplicationApiUri.replace("<clubID>", `${clubID}`);
    }
    return ApplicationApiUri;
}

function createNew(application: MemberApplication): EndpointCall {
    let main_uri = getBaseUri(application.clubId);
    const transport = intoTransport(application);
    const request = createRequest(transport);
    return create(main_uri, request, ApplicationResponseFactory);
}

function load(id: number, hash: string = null, clubID: number = null): EndpointCall {
    let main_uri = getBaseUri(clubID);
    if (clubID !== null) {
        hash = null;
    }
    const hashUri = hash ? '?hashCode=' + hash : '';
    const uri = `${main_uri}/${id}${hashUri}`;
    return retrieve(uri, null, ApplicationResponseFactory);
}

function save(application: MemberApplication): EndpointCall {

    let main_uri = getBaseUri(application.clubId);
    const id = application.id;
    const uri = `${main_uri}/${id}`;
    const transport = intoTransport(application);
    const request = createRequest(transport);
    return update(uri, request, ApplicationResponseFactory);
}

function deleteApplication(application: MemberApplication): EndpointCall {
    let main_uri = getBaseUri(application.clubId);
    const id = application.id;
    const uri = `${main_uri}/${id}`;
    const transport = intoTransport(application);
    const request = createRequest(transport);
    return delete$(uri, request);
}

function sendEmail(application: MemberApplication): EndpointCall {
    let main_uri = getBaseUri(application.clubId);
    const id = application.id;
    const uri = `${main_uri}/${id}/send_reference`;
    const request = createRequest({});
    return create(uri, request);
}

function getPdfUrl(id: number, hash: string = null, clubID: number = null) {
    let main_uri = getBaseUri(clubID);
    const hashUri = hash ? '?hashCode=' + hash : '';
    return `${main_uri}/${id}/pdf${hashUri}`;
}

function loadAllUnapproved(parameters: ODataParameters): EndpointCall {
    const uri = `${ApplicationApiUri}`;
    return retrieveCollection(uri, parameters, null, ApplicationArrayResponseFactory);
}

function loadApplicationsForClub(clubID: number, parameters: ODataParameters): EndpointCall {
    const app_uri = ClubApplicationApiUri.replace("<clubID>", `${clubID}`);
    const uri = `${app_uri}`;
    return retrieveCollection(uri, parameters, null, ApplicationArrayResponseFactory);
}

function intoTransport(storeObject: MemberApplication): Entity {
    const entity = JSON.parse(JSON.stringify(storeObject)); // clones object
    entity.member.birthDate = dateToApiTransportDate(entity.member.birthDate);
    entity.legalRepresentative.birthDate = dateToApiTransportDate(entity.legalRepresentative.birthDate);
    entity.member.ownedIdt = entity.member.ownedIdt || undefined;
    return entity;
}

function fromTransport(entity: MemberApplication): MemberApplication {
    entity.member.birthDate = apiTransportDateToDate(entity.member.birthDate);
    entity.legalRepresentative.birthDate = apiTransportDateToDate(entity.legalRepresentative.birthDate);
    return entity as MemberApplication;
}

const ApplicationResponseFactory: EndpointResponseFactory =
    (response: AjaxResponse) =>
        new ApplicationResponse(response);

class ApplicationResponse extends EntityJSONResponse {
    protected convert(transportObject: Entity): Entity {
        const entityResponse = transportObject as EntityResponse<MemberApplication>;
        entityResponse.entity = fromTransport(entityResponse.entity);
        return entityResponse;
    }
}

const ApplicationArrayResponseFactory: EndpointResponseFactory =
    (response: AjaxResponse) =>
        new ApplicationArrayResponse(response);

class ApplicationArrayResponse extends EntityJSONResponse {
    protected convert(transportObject: Entity): Entity {
        let return_value: MemberApplication[] = [];
        transportObject.forEach((element: MemberApplication) => {
            return_value.push(fromTransport(element));
        });
        return return_value;
    }
}

export {
    createNew,
    load,
    save,
    deleteApplication,
    sendEmail,
    getPdfUrl,
    loadAllUnapproved,
    loadApplicationsForClub,
    fromTransport
}
